import { BASE_CELL_COUNT, GRID_CELL_COUNT, GRID_SIZE, activeCells, cellIndex } from "../gridSize";
import { BLANK, cellValueMinor, isBonusSymbolId, isMysteryId, jackpotAmountMinor, parseSymbolId } from "../rules/symbols";
import { MAX_WIN_MULTIPLIER } from "./offlineMath";

/**
 * The game's rules as pure functions over a board (every cell's id, row-major): what the offline server plays and what
 * the fixtures test checks every scripted round against. The presentation never calls them: what is held, collected and
 * paid always comes from the round.
 */

/** The GROW rows' cells (hidden and blank unless GROW is active). */
export const GROW_CELLS: readonly number[] = Array.from({ length: GRID_CELL_COUNT - BASE_CELL_COUNT }, (_, i) => BASE_CELL_COUNT + i);

/** The active lines' rows (`lineRows`) whose four cells all hold a bonus symbol: the covered lines (decision 56). */
export function coveredLines(board: readonly string[], lineRows: readonly number[]): number[] {
  const rows: number[] = [];
  for (const row of lineRows) {
    let covered = true;
    for (let col = 0; col < GRID_SIZE.columns; col++) if (!isBonusSymbolId(board[cellIndex(row, col)]!)) covered = false;
    if (covered) rows.push(row);
  }
  return rows;
}

/** The cells of `rows`. */
export function cellsOfRows(rows: readonly number[]): number[] {
  return rows.flatMap((row) => Array.from({ length: GRID_SIZE.columns }, (_, col) => cellIndex(row, col)));
}

/** The cells a respin does not roll: every bonus symbol in play, and the GROW rows while they are hidden. */
export function heldCells(board: readonly string[], grown: boolean): number[] {
  const held = activeCells(grown).filter((index) => isBonusSymbolId(board[index]!));
  return grown ? held : [...held, ...GROW_CELLS];
}

export function isFull(board: readonly string[], grown: boolean): boolean {
  return activeCells(grown).every((index) => isBonusSymbolId(board[index]!));
}

/** What a collect landing at `collectorIndex` collects: every coin and jackpot coin in view (no collect, no mystery). */
export function collectSources(board: readonly string[], collectorIndex: number, grown: boolean, betMinor: bigint): { index: number; amountMinor: string }[] {
  const sources: { index: number; amountMinor: string }[] = [];
  for (const index of activeCells(grown)) {
    if (index === collectorIndex) continue;
    const symbol = parseSymbolId(board[index]!);
    if (symbol?.kind === "coin" || symbol?.kind === "jackpot") sources.push({ index, amountMinor: cellValueMinor(board[index]!, betMinor).toString() });
  }
  return sources;
}

/** The board's value: each cell's value times its multiplier (a mystery and an empty collect count nothing yet). */
export function boardValueMinor(board: readonly string[], multipliers: ReadonlyMap<number, number>, grown: boolean, betMinor: bigint): bigint {
  let total = 0n;
  for (const index of activeCells(grown)) total += cellValueMinor(board[index]!, betMinor) * BigInt(multipliers.get(index) ?? 1);
  return total;
}

/**
 * The bonus's pay once the mysteries are revealed: each filled cell's value times its multiplier and the full board's
 * jackpot (at `betMinor`, what the bonus's values are multiples of), capped at `MAX_WIN_MULTIPLIER` times the stake —
 * what the round cost (decision 56: the line's bet times the lines, or a buy's bet).
 */
export function finalPay(board: readonly string[], multipliers: ReadonlyMap<number, number>, grown: boolean, betMinor: bigint, stakeMinor: bigint): {
  cells: { index: number; amountMinor: string }[];
  fullBoard: { jackpot: "grand" | "royal"; amountMinor: string } | null;
  totalMinor: string;
} {
  const cells: { index: number; amountMinor: string }[] = [];
  let total = 0n;
  for (const index of activeCells(grown)) {
    const id = board[index]!;
    if (id === BLANK) continue;
    if (isMysteryId(id)) throw new Error(`finalPay: cell ${index} is still a mystery`);
    const amount = cellValueMinor(id, betMinor) * BigInt(multipliers.get(index) ?? 1);
    cells.push({ index, amountMinor: amount.toString() });
    total += amount;
  }
  let fullBoard: { jackpot: "grand" | "royal"; amountMinor: string } | null = null;
  if (isFull(board, grown)) {
    const jackpot = grown ? "royal" : "grand";
    const amount = jackpotAmountMinor(jackpot, betMinor);
    fullBoard = { jackpot, amountMinor: amount.toString() };
    total += amount;
  }
  const cap = stakeMinor * BigInt(MAX_WIN_MULTIPLIER);
  return { cells, fullBoard, totalMinor: (total > cap ? cap : total).toString() };
}

/** The wire's grid: rows split on '|', cells on ','. */
export function encodeBoard(board: readonly string[]): string {
  const rows: string[] = [];
  for (let row = 0; row < GRID_SIZE.rows; row++) rows.push(board.slice(row * GRID_SIZE.columns, (row + 1) * GRID_SIZE.columns).join(","));
  return rows.join("|");
}

/** A blank board. */
export function blankBoard(): string[] {
  return Array.from({ length: GRID_CELL_COUNT }, () => BLANK);
}
